import React, { useState, useEffect, useRef } from 'react';
import { DashboardLayout } from '../../components/layout';
import { getAllProposals } from '../../api/proposals';
import { ProposalCard } from '../../components/proposal';
import { Card, CardBody, Alert, Button } from '../../components/ui';

const STATUS_TABS = [
  { value: 'pending_qa', label: 'Under Review' },
  { value: 'approved', label: 'Approved' },
  { value: 'sent_to_client', label: 'Sent to Client' },
  { value: 'client_rejected', label: 'Client Feedback' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'rejected,sent_back', label: 'Rejected' },
];

const ProposalListPage = () => {
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [retryKey, setRetryKey] = useState(0);
  const [statusFilter, setStatusFilter] = useState('pending_qa');
  const [lastActiveTab, setLastActiveTab] = useState('pending_qa');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchInputRef = useRef(null);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    const fetchProposals = async () => {
      setLoading(true);
      setFetchError(null);
      try {
        const params = {
          sort_order: 'desc',
          include_analytics: true,
        };
        if (debouncedSearch) {
          params.search = debouncedSearch;
        } else {
          // No active search — use tab filter
          const activeTab = statusFilter || lastActiveTab;
          params.status_filter = activeTab;
        }
        const res = await getAllProposals(params);
        setProposals(res.data?.proposals || res.data || []);
      } catch (err) {
        setFetchError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchProposals();
  }, [statusFilter, debouncedSearch, lastActiveTab, retryKey]);

  const handleTabClick = (tabValue) => {
    setSearchQuery('');
    setDebouncedSearch('');
    setStatusFilter(tabValue);
    setLastActiveTab(tabValue);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setDebouncedSearch('');
    setStatusFilter(lastActiveTab);
    searchInputRef.current?.focus();
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto py-6 px-4">
        {/* Header */}
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Proposals</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Review and manage investment proposals across all leads
            </p>
          </div>
          <div className="relative w-full sm:w-72 sm:flex-shrink-0">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search by lead, company, or title..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 pl-9 pr-9 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 focus:outline-none"
            />
            {searchQuery && (
              <button
                onClick={handleClearSearch}
                aria-label="Clear search"
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Tabular layout: status tabs as column headers, content below */}
        <Card>
          {debouncedSearch ? (
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {loading
                  ? 'Searching...'
                  : <>Showing <span className="font-semibold">{proposals.length}</span> {proposals.length === 1 ? 'result' : 'results'} for &ldquo;<span className="font-medium">{debouncedSearch}</span>&rdquo; across all statuses</>
                }
              </p>
              <button
                onClick={handleClearSearch}
                className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
              >
                Clear search
              </button>
            </div>
          ) : (
            <div className="flex overflow-x-auto md:grid md:grid-cols-6 border-b border-gray-200 dark:border-gray-700">
              {STATUS_TABS.map((tab) => (
                <button
                  key={tab.value}
                  onClick={() => handleTabClick(tab.value)}
                  className={`py-3 px-4 md:px-0 flex-shrink-0 md:flex-shrink whitespace-nowrap text-sm font-medium text-center transition-colors border-b-2 ${
                    statusFilter === tab.value
                      ? 'border-primary-600 text-primary-600 dark:text-primary-400 bg-primary-50/50 dark:bg-primary-900/10'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/50'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          )}
          <CardBody className="p-0">
            {fetchError ? (
              <div className="py-8 px-4">
                <Alert
                  variant={fetchError.code === 'NETWORK_ERROR' ? 'info' : 'error'}
                  title={fetchError.code === 'NETWORK_ERROR' ? 'Connection Issue' : 'Failed to Load Proposals'}
                >
                  {fetchError.message || 'An unexpected error occurred.'}
                </Alert>
                <div className="mt-4 text-center">
                  <Button onClick={() => setRetryKey((k) => k + 1)}>Retry</Button>
                </div>
              </div>
            ) : loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            ) : proposals.length === 0 ? (
              <div className="text-center py-16">
                <svg className="mx-auto h-12 w-12 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <h3 className="mt-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
                  No proposals found
                </h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {debouncedSearch
                    ? `No proposals matching "${debouncedSearch}"`
                    : `No proposals with status \u201c${STATUS_TABS.find((t) => t.value === (statusFilter || lastActiveTab))?.label}\u201d.`
                  }
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-800">
                {proposals.map((p) => (
                  <ProposalCard key={p.id} proposal={p} activeTab={statusFilter} />
                ))}
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default ProposalListPage;
